import { identity } from "lodash-es";

import { translateKey } from "@/redux/modules/value/translations/translations.helpers";
import { Translate } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";

const BASE = "ApiErrors";

const HTTP_CODES: Record<string, string> = {
    "400": "badRequest",
    "401": "unAuthorized",
    "403": "forbidden",
    "404": "notFound",
    "405": "notAllowed",
    "5xx": "serviceUnavailable"
};

const HTTP_METHODS: Record<string, string> = {
    GET: "retrieve",
    POST: "submit",
    PUT: "update",
    DELETE: "delete",
    PATCH: "edit"
};

const getServiceName = (url: Array<string>) => url.at(0);

const getEndpoint = (url: Array<string>) => url.slice(1).join(".");

const getMethodKey = (method: string): string | undefined =>
    HTTP_METHODS[method.toUpperCase()];

const getCodeKey = (status: number): string =>
    HTTP_CODES[status >= 500 ? "5xx" : status.toString()];

const composeKey = (
    keyParts: Array<string | undefined>,
    statusCode?: string
) => {
    const keyWithoutStatusCode =
        BASE +
        keyParts
            .filter(identity)
            .map((part) => `.${part}`)
            .join("");

    return statusCode
        ? `${keyWithoutStatusCode}.${statusCode}`
        : keyWithoutStatusCode;
};

const generateKeys = (
    statusCode: string,
    serviceName?: string,
    endPoint?: string,
    method?: string
) => [
    composeKey([serviceName, endPoint, method], statusCode),
    composeKey([serviceName, endPoint], statusCode),
    composeKey([serviceName, endPoint, method]),
    composeKey([serviceName, endPoint]),
    composeKey([serviceName], statusCode),
    composeKey([], statusCode)
];

const getI18nKey = (
    keys: Array<string>,
    translations: Record<string, string>
): string | undefined =>
    keys.find(
        (key) =>
            !!Object.keys(translations || {}).find(
                (k) => k.toLowerCase() === key.toLowerCase()
            )
    );

const getApiErrorKey = (
    translations: Record<string, string>,
    url: string,
    method: string,
    status: number
) => {
    const urlParts: Array<string> = url
        .replaceAll(`${Config.general["basestore"]}/`, "")
        .replaceAll(`${Config.general["api-base-url"]}/`, "")
        .replaceAll(`web/`, "")
        .split("?")[0]
        .split("/")
        .filter(identity);

    const serviceNameKey = getServiceName(urlParts);
    const endPointKey = getEndpoint(urlParts);
    const methodKey = getMethodKey(method);
    const statusCodeKey = getCodeKey(status);

    const keys = generateKeys(
        statusCodeKey,
        serviceNameKey,
        endPointKey,
        methodKey
    );

    return getI18nKey(keys, translations);
};

const getApiMessageKey = (
    translations: Record<string, string>,
    data: string
) => {
    return getI18nKey([`apiMessages.title.${data}`], translations);
};

export const getRequestErrorMessage = (
    translations: Record<string, string>,
    data: string,
    url: string,
    method: string,
    status: number
): string => {
    const key = data
        ? getApiMessageKey(translations, data)
        : getApiErrorKey(translations, url, method, status);

    return translateKey(translations, key || "apiErrors.general");
};

export const getErrorMessage = (
    t: Translate,
    key: string,
    ...params: Array<string | number | undefined>
): string => {
    return t(key, ...params) || t("apiErrors.general");
};

export const shouldSkip = (
    errorsToSkip: Array<number> = [],
    status: number
): boolean => {
    return errorsToSkip.some((e: any) => e === status);
};
